import React, { useState } from 'react';
import { Badge, Row, Col, Card } from 'react-bootstrap';
import {
    FaUser,
    FaEnvelope,
    FaPhone,
    FaMapMarkerAlt,
    FaCalendarAlt,
    FaBriefcase,
    FaFileAlt,
    FaCheck,
    FaTimes,
    FaClock,
    FaIdCard,
    FaFilePdf,
    FaEye,
    FaSpinner
} from 'react-icons/fa';
import ContractEmployeService from '../../services/ContractEmployeService';
import PDFViewerModal from '../../components/PDFViewerModal';
import '../../assets/css/ContractsPage.css';

const ContractEmployeModal = ({ contract, isOpen, onClose }) => {
    // États pour la génération de PDF (action ponctuelle, différente de la visualisation)
    const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);

    // États pour la visualisation générique des PDF présignés (même pattern que ContractsPage)
    const [showPdfViewer, setShowPdfViewer] = useState(false);
    const [selectedPdfUrl, setSelectedPdfUrl] = useState(null);
    const [selectedPdfContractId, setSelectedPdfContractId] = useState(null);
    const [selectedPdfTitle, setSelectedPdfTitle] = useState('Document PDF');
    const [pdfLoading, setPdfLoading] = useState(false);

    if (!isOpen || !contract) return null;

    /**
     * Génère un nouveau PDF de contrat employé via l'API (action de génération,
     * pas de visualisation d'un fichier déjà existant).
     */
    const handleGenerateContractPdf = async () => {
        setIsGeneratingPdf(true);
        try {
            const response = await ContractEmployeService.generateContractEmployePdf(contract.id);
            const blob = new Blob([response], { type: 'application/pdf' });
            const url = URL.createObjectURL(blob);
            setSelectedPdfUrl(url);
            setSelectedPdfTitle(`Contrat Employé Généré - ${contract.contractRef}`);
            setSelectedPdfContractId(contract.contractRef);
            setShowPdfViewer(true);
        } catch (error) {
            console.error("Erreur lors de la génération du PDF:", error);
        } finally {
            setIsGeneratingPdf(false);
        }
    };

    /**
     * Fonction générique pour visualiser un PDF déjà existant (présigné ou non).
     * Télécharge via fetch pour contourner les problèmes CORS, avec fallback
     * sur l'URL brute si le fetch échoue.
     */
    const handleViewPdf = async (url, contractRef, title = 'Document PDF') => {
        if (!url) {
            alert('Aucun PDF disponible pour ce document');
            return;
        }

        setPdfLoading(true);
        setSelectedPdfTitle(title);
        setSelectedPdfContractId(contractRef);

        try {
            const response = await fetch(url, {
                mode: 'cors',
                headers: {
                    'Accept': 'application/pdf',
                }
            });

            if (!response.ok) {
                throw new Error(`Erreur HTTP: ${response.status}`);
            }

            const blob = await response.blob();
            const blobUrl = URL.createObjectURL(blob);
            setSelectedPdfUrl(blobUrl);
            setShowPdfViewer(true);
        } catch (error) {
            console.error('Erreur lors du chargement du PDF:', error);
            setSelectedPdfUrl(url);
            setShowPdfViewer(true);
        } finally {
            setPdfLoading(false);
        }
    };

    // Contrat généré (avant signature)
    const handleViewGeneratedContract = () => {
        const url = contract.generatedContractPresigned || contract.generatedContract;
        handleViewPdf(url, contract.contractRef, `Contrat Généré - ${contract.contractRef}`);
    };

    // Document signé par l'employé
    const handleViewEmployeSignedDoc = () => {
        const url = contract.contractDocEmployeSignedPresigned || contract.contractDocEmployeSigned;
        handleViewPdf(url, contract.contractRef, `Contrat Signé par l'Employé - ${contract.contractRef}`);
    };

    // Contrat actif (signé par DHS)
    const handleViewActiveContract = () => {
        const url = contract.contractEmployeActivePresigned || contract.contractEmployeActive;
        handleViewPdf(url, contract.contractRef, `Contrat Actif - ${contract.contractRef}`);
    };

    const closePdfViewer = () => {
        setShowPdfViewer(false);
        if (selectedPdfUrl && selectedPdfUrl.startsWith('blob:')) {
            URL.revokeObjectURL(selectedPdfUrl);
        }
        setSelectedPdfUrl(null);
        setSelectedPdfContractId(null);
        setSelectedPdfTitle('Document PDF');
        setPdfLoading(false);
    };

    const formatDate = (timestamp) => {
        if (!timestamp) return 'Non spécifié';
        return new Date(timestamp).toLocaleDateString('fr-FR', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const formatDesiredStartDate = (dateData) => {
        if (!dateData) return 'Non spécifiée';

        try {
            let date;

            if (typeof dateData === 'number' && dateData > 1000000000000) {
                date = new Date(dateData);
            }
            else if (Array.isArray(dateData)) {
                const [year, month, day] = dateData;
                date = new Date(year, month - 1, day);
            }
            else if (typeof dateData === 'string') {
                if (dateData.includes('T')) {
                    date = new Date(dateData);
                } else if (dateData.includes('-')) {
                    const [year, month, day] = dateData.split('-');
                    date = new Date(year, month - 1, day);
                } else {
                    return 'Format non supporté';
                }
            }
            else if (dateData instanceof Date) {
                date = dateData;
            } else {
                return 'Format non supporté';
            }

            if (isNaN(date.getTime())) {
                return 'Date invalide';
            }

            return date.toLocaleDateString('fr-FR', {
                weekday: 'long',
                day: 'numeric',
                month: 'long',
                year: 'numeric'
            });

        } catch (error) {
            console.error('Erreur de formatage:', error);
            return 'Erreur';
        }
    };

    const getFullName = (user) => {
        if (!user) return 'Information non disponible';
        return `${user.firstName} ${user.lastName}`.trim();
    };

    const getEmail = (user) => {
        return user?.email || 'Email non disponible';
    };

    const getPhone = (user) => {
        return user?.phoneNumber || 'Téléphone non disponible';
    };

    const getLocation = (user) => {
        if (!user) return 'Localisation non disponible';
        return `${user.city || ''}, ${user.country || ''}`.trim().replace(/^,\s*|\s*,$/g, '');
    };

    const getAvatarUrl = (user) => {
        return user?.profilePictureUrl || '/assets/images/logo/logo.png';
    };

    const DisplayField = ({ label, value, icon, emptyText = "Non spécifié" }) => (
        <div className="display-field mb-3">
            <div className="field-label">
                {icon} {label}
            </div>
            <div className="field-value bg-white rounded p-2">
                {value || emptyText}
            </div>
        </div>
    );

    return (
        <>
            {/* Modal principal */}
            <div
                className="contract-modal-overlay show"
                style={{
                    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                    backgroundColor: 'rgba(0,0,0,0.8)', zIndex: 1050, overflowY: 'auto'
                }}
                onClick={onClose}
            >
                <div
                    className="contract-modal-container"
                    style={{
                        position: 'absolute', bottom: 0, left: 0, right: 0,
                        backgroundColor: 'white', borderRadius: '24px 24px 0 0',
                        maxHeight: '95vh', overflowY: 'auto', animation: 'slideUp 0.4s ease'
                    }}
                    onClick={(e) => e.stopPropagation()}
                >
                    {/* Header fixe */}
                    <div className="modal-header-fixed" style={{
                        position: 'sticky', top: 0, left: 0, right: 0, zIndex: 100,
                        backgroundColor: 'white', borderBottom: '1px solid #e2e8f0',
                        borderTopLeftRadius: '24px', borderTopRightRadius: '24px', padding: '16px 24px'
                    }}>
                        <div className="d-flex justify-content-between align-items-center">
                            <div>
                                <h4 className="fw-bold mb-0 text-dark">
                                    <FaFileAlt className="me-2 text-primary" />
                                    Détails du contrat employé
                                </h4>
                                <p className="text-muted small mt-1 mb-0">
                                    Référence: {contract.contractRef}
                                </p>
                            </div>
                            <button className="btn-close" onClick={onClose}></button>
                        </div>
                    </div>

                    {/* Contenu principal */}
                    <div className="p-4">
                        {/* Informations des parties */}
                        <Row className="g-4 mb-4">
                            <Col md={6}>
                                <div className="bg-light rounded-3 p-3 h-100">
                                    <h5 className="fw-bold mb-3 text-primary">
                                        <FaUser className="me-2" />
                                        Informations du client
                                    </h5>
                                    <div className="text-center mb-3">
                                        <img
                                            src={getAvatarUrl(contract.client?.user)}
                                            alt={getFullName(contract.client?.user)}
                                            className="rounded-circle"
                                            style={{
                                                width: '80px',
                                                height: '80px',
                                                objectFit: 'cover',
                                                border: '3px solid #28a745'
                                            }}
                                            onError={(e) => {
                                                e.target.src = '/assets/images/logo/logo.png';
                                            }}
                                        />
                                    </div>
                                    <div className="text-center">
                                        <h6 className="text-success fw-bold mb-2">
                                            {getFullName(contract.client?.user)}
                                        </h6>
                                        <div className="small text-muted">
                                            <FaEnvelope className="me-1" /> {getEmail(contract.client?.user)}<br />
                                            <FaPhone className="me-1" /> {getPhone(contract.client?.user)}<br />
                                            <FaMapMarkerAlt className="me-1" /> {getLocation(contract.client?.user)}
                                        </div>
                                    </div>
                                </div>
                            </Col>

                            <Col md={6}>
                                <div className="bg-light rounded-3 p-3 h-100">
                                    <h5 className="fw-bold mb-3 text-success">
                                        <FaBriefcase className="me-2" />
                                        Informations du professionnel
                                    </h5>
                                    <div className="text-center mb-3">
                                        <img
                                            src={getAvatarUrl(contract.professional?.user)}
                                            alt={getFullName(contract.professional?.user)}
                                            className="rounded-circle"
                                            style={{
                                                width: '80px',
                                                height: '80px',
                                                objectFit: 'cover',
                                                border: '3px solid #28a745'
                                            }}
                                            onError={(e) => {
                                                e.target.src = '/assets/images/logo/logo.png';
                                            }}
                                        />
                                    </div>
                                    <div className="text-center">
                                        <h6 className="text-success fw-bold mb-2">
                                            {getFullName(contract.professional?.user)}
                                        </h6>
                                        <div className="small text-muted">
                                            <FaEnvelope className="me-1" /> {getEmail(contract.professional?.user)}<br />
                                            <FaPhone className="me-1" /> {getPhone(contract.professional?.user)}<br />
                                            <FaMapMarkerAlt className="me-1" /> {getLocation(contract.professional?.user)}
                                        </div>
                                    </div>
                                </div>
                            </Col>
                        </Row>

                        {/* Détails du contrat */}
                        <div className="bg-light rounded-3 p-3 mb-4">
                            <h5 className="fw-bold mb-3 text-info">
                                <FaCalendarAlt className="me-2" />
                                Détails du contrat employé
                            </h5>
                            <Row className="g-3">
                                <Col md={6}>
                                    <DisplayField
                                        label="Date de début"
                                        value={formatDesiredStartDate(contract.startDate)}
                                        icon={<FaCalendarAlt className="text-primary" />}
                                    />
                                    <DisplayField
                                        label="Durée"
                                        value={`${contract.contractDuration} mois`}
                                        icon={<FaCalendarAlt className="text-primary" />}
                                    />
                                    <DisplayField
                                        label="Date de fin"
                                        value={formatDesiredStartDate(contract.endDate)}
                                        icon={<FaCalendarAlt className="text-primary" />}
                                    />
                                </Col>
                                <Col md={6}>
                                    {/* Contrat généré */}
                                    <div className="display-field mb-3">
                                        <div className="field-label">
                                            <FaFilePdf className="me-1 text-danger" /> Contrat Généré
                                        </div>
                                        <div className="field-value bg-white rounded p-2">
                                            {(contract.generatedContractPresigned || contract.generatedContract) ? (
                                                <div className="d-flex align-items-center gap-2">
                                                    <FaCheck className="text-success" />
                                                    <button
                                                        className="btn btn-link text-primary p-0 text-decoration-none"
                                                        onClick={handleViewGeneratedContract}
                                                        disabled={pdfLoading}
                                                    >
                                                        {pdfLoading ? (
                                                            <><FaSpinner className="fa-spin me-1" /> Chargement...</>
                                                        ) : (
                                                            <><FaEye className="me-1" /> Voir le contrat généré</>
                                                        )}
                                                    </button>
                                                </div>
                                            ) : (
                                                <div className="d-flex align-items-center gap-2">
                                                    <FaTimes className="text-danger" />
                                                    <span className="text-muted">Non généré</span>
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {/* Signé par l'employé */}
                                    <div className="display-field mb-3">
                                        <div className="field-label">
                                            <FaUser className="me-1" /> Signé par l'employé
                                        </div>
                                        <div className="field-value bg-white rounded p-2">
                                            {contract.employeSigned ? (
                                                <div className="d-flex align-items-center gap-2">
                                                    <FaCheck className="text-success" />
                                                    {(contract.contractDocEmployeSignedPresigned || contract.contractDocEmployeSigned) ? (
                                                        <button
                                                            className="btn btn-link text-primary p-0 text-decoration-none"
                                                            onClick={handleViewEmployeSignedDoc}
                                                            disabled={pdfLoading}
                                                        >
                                                            {pdfLoading ? (
                                                                <><FaSpinner className="fa-spin me-1" /> Chargement...</>
                                                            ) : (
                                                                <>Signé le {formatDate(contract.employeSignDate)} - Voir le PDF</>
                                                            )}
                                                        </button>
                                                    ) : (
                                                        <span>Signé le {formatDate(contract.employeSignDate)}</span>
                                                    )}
                                                </div>
                                            ) : (
                                                <div className="d-flex align-items-center gap-2">
                                                    <FaClock className="text-warning" />
                                                    <span className="text-muted">En attente de signature</span>
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {/* Signé par DHS */}
                                    <div className="display-field mb-3">
                                        <div className="field-label">
                                            <FaIdCard className="me-1" /> Signé par Driven Home Services
                                        </div>
                                        <div className="field-value bg-white rounded p-2">
                                            {contract.dhsSignedEmploye ? (
                                                <div className="d-flex align-items-center gap-2">
                                                    <FaCheck className="text-success" />
                                                    {(contract.contractEmployeActivePresigned || contract.contractEmployeActive) ? (
                                                        <button
                                                            className="btn btn-link text-primary p-0 text-decoration-none"
                                                            onClick={handleViewActiveContract}
                                                            disabled={pdfLoading}
                                                        >
                                                            {pdfLoading ? (
                                                                <><FaSpinner className="fa-spin me-1" /> Chargement...</>
                                                            ) : (
                                                                <>Signé le {formatDate(contract.dhsSignDate)} - Voir le PDF</>
                                                            )}
                                                        </button>
                                                    ) : (
                                                        <span>Signé le {formatDate(contract.dhsSignDate)}</span>
                                                    )}
                                                </div>
                                            ) : (
                                                <div className="d-flex align-items-center gap-2">
                                                    <FaClock className="text-warning" />
                                                    <span className="text-muted">En attente de signature</span>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </Col>
                            </Row>
                        </div>

                        {/* Informations du service */}
                        {contract.serviceName && (
                            <div className="bg-light rounded-3 p-3 mb-4">
                                <h5 className="fw-bold mb-3 text-primary">
                                    <FaBriefcase className="me-2" />
                                    Informations du Service sélectionné
                                </h5>
                                <div className="bg-white rounded p-3">
                                    <h6 className="text-primary fw-bold mb-2">{contract.serviceName}</h6>
                                    <div className="d-flex flex-wrap gap-2">
                                        {contract.subServiceName && (
                                            <Badge bg="primary" className="rounded-pill px-3 py-2">
                                                <FaCheck className="me-1" /> {contract.subServiceName}
                                            </Badge>
                                        )}
                                        {contract.serviceFee && (
                                            <Badge bg="success" className="rounded-pill px-3 py-2">
                                                <FaCheck className="me-1" /> {new Intl.NumberFormat('fr-FR').format(contract.serviceFee)} FCFA
                                            </Badge>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Informations de localisation */}
                        {contract.serviceLocation && (
                            <div className="bg-light rounded-3 p-3 mb-4">
                                <h5 className="fw-bold mb-3 text-warning">
                                    <FaMapMarkerAlt className="me-2" />
                                    Localisation du service
                                </h5>
                                <div className="bg-white rounded p-2">
                                    <FaMapMarkerAlt className="me-2 text-danger" />
                                    {contract.serviceLocation}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Modal générique pour afficher n'importe quel PDF présigné (même composant que ContractsPage) */}
            <PDFViewerModal
                show={showPdfViewer}
                onHide={closePdfViewer}
                pdfUrl={selectedPdfUrl}
                contractId={selectedPdfContractId}
                title={selectedPdfTitle}
                isLoading={pdfLoading}
            />

            <style jsx>{`
                @keyframes slideUp {
                    from { transform: translateY(100%); }
                    to { transform: translateY(0); }
                }

                @keyframes fadeIn {
                    from { opacity: 0; }
                    to { opacity: 1; }
                }

                .contract-modal-overlay {
                    animation: fadeIn 0.3s ease;
                }

                .modal-header-fixed, .modal-footer-fixed {
                    box-shadow: 0 2px 8px rgba(0,0,0,0.05);
                }

                ::-webkit-scrollbar {
                    width: 6px;
                }

                ::-webkit-scrollbar-track {
                    background: #f1f1f1;
                }

                ::-webkit-scrollbar-thumb {
                    background: #888;
                    border-radius: 3px;
                }

                ::-webkit-scrollbar-thumb:hover {
                    background: #555;
                }

                .field-label {
                    font-size: 0.85rem;
                    font-weight: 600;
                    color: #2c3e50;
                    margin-bottom: 0.5rem;
                }

                .field-value {
                    font-size: 0.95rem;
                    color: #1a202c;
                }

                @keyframes fa-spin {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                }

                .fa-spin {
                    animation: fa-spin 1s linear infinite;
                }
            `}</style>
        </>
    );
};

export default ContractEmployeModal;